import type { ChangeEvent } from 'react';
import { Component } from 'react';
import { Navigate } from 'react-router';
import { enableLoginRememberEmail } from '@gonfalon/dogfood-flags/dogfood-flags';
import { FieldError, Form, FormError, FormGroup, FormHint } from '@gonfalon/legacy-forms/components';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { toCheckYourEmail } from '@gonfalon/navigator/navigators';
import { Restrict } from '@gonfalon/permissions/restrict';
import { getSignupAttributionSearchParams } from '@gonfalon/signup/agentUtmParams';
import { Checkbox } from '@launchpad-ui/components';
// oxlint-disable-next-line no-restricted-imports
import { Label, TextField } from '@launchpad-ui/core';
import cx from 'clsx';

import { SubmitButton } from '#components/ui/buttons/SubmitButton';
import type { WithFormEnhancerProps } from '#components/withForm';
import type { LoginInfo } from '#utils/loginUtils';

import '#stylesheets/components/LoginForm.css';

export type LoginFormProps = WithFormEnhancerProps<LoginInfo> & {
  hiddenFields?: string[];
  requirePassword: boolean;
  beastMode?: boolean;
  isUnverifiedEmail?: boolean;
  submitButtonLabel?: string; // Overrides the default "Log in" label
  rememberedEmail?: string;
  rememberMe?: boolean;
  onRememberMeChange?: (checked: boolean) => void;
};

type StateProps = {
  password: string;
};

export class LoginForm extends Component<LoginFormProps, StateProps> {
  // To support password managers, we track the password state locally
  // until it is required to make sure the user does not have to re-enter it.
  state = {
    password: '',
  };

  passwordRef?: HTMLInputElement | null;

  componentDidUpdate(prevProps: LoginFormProps) {
    if (!prevProps.requirePassword && this.props.requirePassword) {
      setTimeout(() => this.passwordRef?.focus());
      if (this.state.password) {
        this.props.onChange('password', this.state.password);
      }
    }
  }

  render() {
    const { hiddenFields, requirePassword, modified, onBlur, onChange, onSubmit, beastMode, isUnverifiedEmail } =
      this.props;
    const passwordClasses = cx('fs-exclude', 'LoginForm-password', {
      'is-required': requirePassword,
    });

    const renderSubmitButtonLabel = (config: { requirePassword?: boolean; submitButtonLabel?: string }) => {
      if (config?.submitButtonLabel) {
        return config.submitButtonLabel;
      } else if (config?.requirePassword) {
        return msg('login.signIn.signIn');
      } else {
        return msg('login.signIn.continue');
      }
    };

    if (isUnverifiedEmail) {
      return (
        <Navigate
          to={toCheckYourEmail({ search: getSignupAttributionSearchParams() })}
          state={{ email: modified.username }}
        />
      );
    }

    return (
      <Form onSubmit={onSubmit} onBlurCapture={onBlur} className="LoginForm" hasIncreasedErrorMargin>
        <FormError />

        {!hiddenFields?.includes('email') && (
          <FormGroup name="username">
            <Restrict
              isRestricted={!!beastMode}
              tooltip={msg('login.signIn.reauthenticateTooltip')}
              tooltipOptions={{
                rootElementStyle: {
                  display: 'block',
                },
              }}
              willDisable
            >
              {(disabled) => (
                <div>
                  <Label htmlFor="username">
                    <Message id="login.signIn.emailLabel" />
                  </Label>
                  <TextField
                    required
                    disabled={disabled}
                    id="username"
                    name="username"
                    data-test-id="username"
                    autoComplete="email"
                    value={modified.username}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange('username', e.target.value)}
                  />
                </div>
              )}
            </Restrict>
            <FieldError className="error" name="username" />
            {enableLoginRememberEmail() && !beastMode && (
              <Checkbox
                name="rememberMe"
                isSelected={!!this.props.rememberMe}
                onChange={(isSelected) => this.props.onRememberMeChange?.(isSelected)}
              >
                <Message id="login.signIn.rememberMe" />
              </Checkbox>
            )}
          </FormGroup>
        )}
        <FormGroup
          name="password"
          className={passwordClasses}
          aria-hidden={!requirePassword}
          data-test-id="login-password"
        >
          <Label htmlFor="password">
            <Message id="login.signIn.passwordLabel" />
          </Label>
          <TextField
            required={!!requirePassword}
            ref={(c) => {
              this.passwordRef = c;
            }}
            id="password"
            name="password"
            data-test-id="password"
            type="password"
            autoComplete="current-password"
            tabIndex={requirePassword ? 0 : -1}
            value={requirePassword ? modified.password : this.state.password}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              requirePassword ? onChange('password', e.target.value) : this.setState({ password: e.target.value })
            }
          />
          <FormHint>
            <a tabIndex={requirePassword ? 0 : -1} href="/forgot">
              <Message id="login.signIn.forgotPassword" />
            </a>
          </FormHint>
          <FieldError className="error" name="password" />
        </FormGroup>

        <FormGroup>
          <SubmitButton
            data-test-id="signin-button"
            isDisabled={false}
            loadingText={requirePassword ? msg('login.signIn.signingIn') : undefined}
            className="LoginForm-submit"
          >
            {renderSubmitButtonLabel({ requirePassword, submitButtonLabel: this.props.submitButtonLabel })}
          </SubmitButton>
        </FormGroup>
      </Form>
    );
  }
}
